import * as coreEntities from 'core/store/index';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAnalyticsFilters } from '../../../contexts/AnalyticsFiltersContext';
import analyticsService from '../../../services/analyticsService';
import { RADAR_ALERT_STATUS } from '../../../types/radar.constants';
import type { RadarAlert } from '../../../types/radar';

type UseAlertDetailDrawerProps = {
  alert: RadarAlert | null;
  onStatusChanged?: (annotationId: string, newStatus: string, reviewerName: string) => void;
};

type UseAlertDetailDrawerReturn = {
  handleDismiss: () => void;
  handleMarkAsAttended: () => void;
  updating: boolean;
};

function useAlertDetailDrawer({ alert, onStatusChanged }: UseAlertDetailDrawerProps): UseAlertDetailDrawerReturn {
  const currentUser = useSelector((state: any) => coreEntities.auth.selectors.getUser(state));
  const { isMockMode } = useAnalyticsFilters();
  const [updating, setUpdating] = useState<boolean>(false);

  const getCurrentUserName = useCallback((): string => {
    if (!currentUser) return '';
    return `${currentUser.name || ''} ${currentUser.lastname || ''}`.trim();
  }, [currentUser]);

  const handleMarkAsAttended = useCallback(async () => {
    if (!alert?.id) return;
    if (isMockMode) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(alert.id, { status: RADAR_ALERT_STATUS.REVIEWED });
      onStatusChanged?.(alert.id, RADAR_ALERT_STATUS.REVIEWED, getCurrentUserName());
    } catch (err) {
      console.error('Failed to mark as attended:', err);
    } finally {
      setUpdating(false);
    }
  }, [alert?.id, isMockMode, onStatusChanged, getCurrentUserName]);

  const handleDismiss = useCallback(async () => {
    if (!alert?.id) return;
    if (isMockMode) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(alert.id, { status: RADAR_ALERT_STATUS.DISMISSED });
      onStatusChanged?.(alert.id, RADAR_ALERT_STATUS.DISMISSED, getCurrentUserName());
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setUpdating(false);
    }
  }, [alert?.id, isMockMode, onStatusChanged, getCurrentUserName]);

  return { handleDismiss, handleMarkAsAttended, updating };
}

export default useAlertDetailDrawer;
